import { useCallback } from 'react';
import { useStatsigClient } from '@statsig/react-bindings';
import type { INudgeAnalyticsConfig, INudgeConfig } from '../types';

/**
 * Resolves analytics config with defaults.
 *
 * @param config - Optional partial analytics config
 * @param nudgeId - Nudge identifier used as fallback event prefix
 * @returns Fully resolved config
 */
function resolveConfig(
  config: INudgeAnalyticsConfig | undefined,
  nudgeId: string,
): Required<Pick<INudgeAnalyticsConfig, 'enabled' | 'eventPrefix'>> &
  INudgeAnalyticsConfig {
  return {
    enabled: true,
    eventPrefix: `nudge_${nudgeId}`,
    ...config,
  };
}

/**
 * Builds metadata for a nudge analytics event.
 *
 * @param nudgeId - Nudge identifier
 * @param extra - Additional metadata
 * @returns Metadata record
 */
function buildMetadata(
  nudgeId: string,
  extra?: Record<string, string>,
): Record<string, string> {
  return { nudgeId, ...extra };
}

/**
 * Hook that provides analytics event emitters for nudge lifecycle events.
 *
 * @param nudge - The nudge configuration
 * @returns Object with methods to track nudge events
 */
export function useNudgeAnalytics(nudge: INudgeConfig) {
  const { logEvent } = useStatsigClient();
  const config = resolveConfig(nudge.analytics, nudge.id);

  // Track the nudge shown event
  const trackShown = useCallback(() => {
    if (!config.enabled) return;
    logEvent(
      `${config.eventPrefix}_shown`,
      nudge.id,
      buildMetadata(nudge.id, config.metadata),
    );
  }, [config.enabled, config.eventPrefix, config.metadata, logEvent, nudge.id]);

  const trackDismissed = useCallback(() => {
    if (!config.enabled) return;
    logEvent(
      `${config.eventPrefix}_dismissed`,
      nudge.id,
      buildMetadata(nudge.id, config.metadata),
    );
  }, [config.enabled, config.eventPrefix, config.metadata, logEvent, nudge.id]);

  const trackActionClicked = useCallback(() => {
    if (!config.enabled) return;
    logEvent(
      `${config.eventPrefix}_action_clicked`,
      nudge.id,
      buildMetadata(nudge.id, config.metadata),
    );
  }, [config.enabled, config.eventPrefix, config.metadata, logEvent, nudge.id]);

  return { trackShown, trackDismissed, trackActionClicked };
}
